import clsx from 'clsx';
import { useState } from 'react';
import BaseTooltip from '@/global/Tooltip/BaseTooltip';

type TabSelectorVariant = 'primary' | 'secondary' | 'tertiary' | 'warning' | 'error';
type TabSelectorSize = '2xs' | 'xs' | 'sm' | 'md' | 'lg';

const VARIANT_SELECTED_BG: Record<TabSelectorVariant, string> = {
  primary: 'var(--blue-color)',
  secondary: '#64748b',
  tertiary: 'var(--blue-color)',
  warning: '#d97706',
  error: '#dc2626',
};

const TRACK_SIZE_CLASS: Record<TabSelectorSize, string> = {
  '2xs': 'rounded-md p-1 gap-1.5',
  xs: 'rounded-lg p-0.5 gap-0.5',
  sm: 'rounded-xl p-0.5 gap-0.5',
  md: 'rounded-xl p-1 gap-1',
  lg: 'rounded-2xl p-1.5 gap-1.5',
};

const BUTTON_SIZE_CLASS: Record<TabSelectorSize, string> = {
  '2xs': 'rounded-md px-3 py-2 text-xs leading-tight font-semibold uppercase tracking-wide',
  xs: 'rounded-md p-2 text-xs leading-snug font-semibold',
  sm: 'rounded-lg px-4 py-2 text-xs font-semibold',
  md: 'rounded-lg px-6 py-2.5 text-sm font-semibold',
  lg: 'rounded-lg px-8 py-3 text-base font-semibold',
};

type TabSelectorOption = {
  label: string;
  value: string;
  disabled?: boolean;
  disabledTooltip?: string;
};

type TabSelectorProps = {
  options: TabSelectorOption[];
  variant?: TabSelectorVariant;
  size?: TabSelectorSize;
  value?: string;
  onChange: (value: string) => void;
  className?: string;
  justify?: 'center' | 'start' | 'end';
};

type TabSelectorOptionItemProps = {
  option: TabSelectorOption;
  isSelected: boolean;
  selectedBg: string;
  size: TabSelectorSize;
  onSelect: (option: TabSelectorOption) => void;
};

const TabSelectorOptionItem = ({
  option,
  isSelected,
  selectedBg,
  size,
  onSelect,
}: TabSelectorOptionItemProps) => {
  const button = (
    <button
      type="button"
      className={clsx(
        'transition-all text-muted-foreground hover:text-foreground',
        BUTTON_SIZE_CLASS[size],
        option.disabled && 'cursor-not-allowed opacity-50 hover:text-muted-foreground',
      )}
      onClick={() => onSelect(option)}
      disabled={option.disabled}
      aria-disabled={option.disabled || undefined}
      style={{
        backgroundColor: isSelected ? selectedBg : 'transparent',
        color: isSelected ? 'white' : 'var(--gray-color)',
      }}
    >
      {option.label}
    </button>
  );

  const content = <span className="inline-flex">{button}</span>;

  if (option.disabled && option.disabledTooltip) {
    return (
      <BaseTooltip content={option.disabledTooltip} position="top">
        {content}
      </BaseTooltip>
    );
  }

  return content;
};

const TabSelector = ({
  className = '',
  justify = 'center',
  variant = 'primary',
  size = 'md',
  options,
  value: controlledValue,
  onChange,
}: TabSelectorProps) => {
  const [internalValue, setInternalValue] = useState(options[0].value);
  const selectedValue = controlledValue !== undefined ? controlledValue : internalValue;

  const handleTabClick = (option: TabSelectorOption) => {
    if (option.disabled) return;
    setInternalValue(option.value);
    onChange(option.value);
  };

  const justifyClass =
    justify === 'start' ? 'justify-start' : justify === 'end' ? 'justify-end' : 'justify-center';

  const selectedBg = VARIANT_SELECTED_BG[variant] ?? VARIANT_SELECTED_BG.primary;

  return (
    <>
      <div className={clsx('flex', justifyClass)}>
        <div className={clsx('inline-flex bg-gray-100', TRACK_SIZE_CLASS[size], className)}>
          {options.map(option => (
            <TabSelectorOptionItem
              key={option.value}
              option={option}
              isSelected={selectedValue === option.value}
              selectedBg={selectedBg}
              size={size}
              onSelect={handleTabClick}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default TabSelector;
